import { Route, Navigate, Outlet } from 'react-router-dom';
import { ProtectedRoute } from 'ProtectedRoute';
import { type RouteRegistryEntry } from 'FrontRoyalRouteRegistry/FrontRoyalRouteRegistry.types';
import { AdminUsersComponent } from './components/AdminUsersComponent';
import { AdminMba } from './components/AdminMba';

/*
    WARNING: anything imported into this file will end up in the main javascript bundle. Our
    intention has always been to keep admin code out of the main bundle and lazy-load it
    via loadAdminDependencies.

    As long as our admin code is all still in angular, we are still following that vision, because
    we're not importing the angular module here. Instead, we're ensuring that the angular code is
    loaded via useLoadAdminDependencies before trying to render anything.

    Top-level admin components that have moved to react keep that guarantee by loading through a
    react-router `lazy` route instead, so their code never enters the main bundle. See
    getLazyBatchEditUsers below.
*/

const USERS_BASE = '/admin/users';

// Define all admin route paths
export const ADMIN_ROUTES = {
    USERS: {
        path: USERS_BASE,
        APPLICANTS: { path: `${USERS_BASE}/applicants` },
        ALL: { path: `${USERS_BASE}/all` },
    },
    BATCH_USERS: { path: '/admin/batch-users/:section?' },
    MBA: { path: '/admin/mba/:section?' },
    GROUPS_REDIRECT: { path: '/admin/groups' },
    GLOBAL_METADATA: { path: '/admin/global-metadata' },
};

/*
    Batch Edit Users is a React page, so it is loaded as its own chunk rather than imported
    above. That keeps it, and MUI, out of the main bundle that every student downloads. This
    follows GradingPortal/GradingPortalRoutes.tsx.
*/
async function getLazyBatchEditUsers() {
    const { BatchEditUsers } = await import('BatchEditUsers/BatchEditUsers');
    return {
        Component: () => (
            <ProtectedRoute accessPolicy="hasAdminAccess">
                <BatchEditUsers />
            </ProtectedRoute>
        ),
    };
}

// The global metadata editor is a React page too, so it loads as its own chunk for the same reason.
async function getLazyAdminGlobalMetadata() {
    const { default: AdminGlobalMetadata } = await import('AdminGlobalMetadata');
    return {
        Component: () => (
            <ProtectedRoute accessPolicy="hasAdminAccess">
                <AdminGlobalMetadata />
            </ProtectedRoute>
        ),
    };
}

// Wrapper component that applies the 'admin' class to app-root
function AdminRoutesWrapper() {
    return (
        <div className="admin">
            <Outlet />
        </div>
    );
}

export function configureAdminRoutes(): RouteRegistryEntry[] {
    return [
        {
            routes: ADMIN_ROUTES,
            config: {
                errorHandlingRules: 'admin',
            },
        },
    ];
}

export function getAdminRoutes() {
    return (
        <Route id="admin-routes" element={<AdminRoutesWrapper />}>
            {/* Admin users routes */}
            <Route
                path={ADMIN_ROUTES.USERS.APPLICANTS.path}
                element={<Navigate to={ADMIN_ROUTES.USERS.path} replace />}
            />
            <Route path={ADMIN_ROUTES.USERS.ALL.path} element={<Navigate to={ADMIN_ROUTES.USERS.path} replace />} />
            <Route
                path={ADMIN_ROUTES.USERS.path}
                element={
                    <ProtectedRoute accessPolicy="hasSuperEditorOrInterviewerAccess">
                        <AdminUsersComponent />
                    </ProtectedRoute>
                }
            />

            {/* Batch edit users routes */}
            <Route path="/admin/batch-users" element={<Navigate to="/admin/batch-users/select-users" replace />} />
            <Route path={ADMIN_ROUTES.BATCH_USERS.path} lazy={getLazyBatchEditUsers} />

            {/* Admin MBA routes */}
            <Route path="/admin/mba" element={<Navigate to="/admin/mba/calendar" replace />} />
            <Route path={ADMIN_ROUTES.GROUPS_REDIRECT.path} element={<Navigate to="/admin/mba/groups" replace />} />
            <Route
                path={ADMIN_ROUTES.MBA.path}
                element={
                    <ProtectedRoute accessPolicy="hasAdminAccess">
                        <AdminMba />
                    </ProtectedRoute>
                }
            />

            {/* Admin global metadata route */}
            <Route path={ADMIN_ROUTES.GLOBAL_METADATA.path} lazy={getLazyAdminGlobalMetadata} />
        </Route>
    );
}
